import { forwardRef, type PropsWithChildren } from "react";
import { Label, Text } from "react-aria-components";
import { useDesignTokens } from "@boi/react-design-tokens";

import * as S from "./InputLabelTopV2.styled";
import { Typography } from "../Typography";
import {
  InputLabelTopTooltip,
  type InputLabelTopTooltipProps,
} from "../InputLabelTopTooltip";

type TooltipProps = Omit<InputLabelTopTooltipProps, "otherIconButtonProps">;

export type InputLabelTopV2Props = PropsWithChildren<{
  label?: string;
  labelTooltipProps?: TooltipProps;
  labelHint?: string;
  labelHintTooltipProps?: TooltipProps;
}>;

const InputLabelTopV2 = forwardRef<HTMLDivElement, InputLabelTopV2Props>(
  (props, ref) => {
    const {
      label,
      labelTooltipProps,
      labelHint,
      labelHintTooltipProps,
      children,
    } = props;

    const { tokens } = useDesignTokens();
    const { inputLabelTop } = tokens;

    return (
      <S.Container ref={ref}>
        {(label || labelHint) && (
          <div>
            {label && (
              <Typography
                component="div"
                color={inputLabelTop.label.color}
                variant={inputLabelTop.label.typography.variant}
                fontFamily={inputLabelTop.label.typography.fontFamily}
              >
                <Label>{label}</Label>

                {labelTooltipProps && (
                  <InputLabelTopTooltip
                    {...labelTooltipProps}
                    otherIconButtonProps={{
                      variant: "default",
                      typographyVariant: inputLabelTop.label.typography.variant,
                    }}
                  />
                )}
              </Typography>
            )}

            {labelHint && (
              <Typography
                component="div"
                color={inputLabelTop.labelHint.color}
                variant={inputLabelTop.labelHint.typography.variant}
                fontFamily={inputLabelTop.labelHint.typography.fontFamily}
              >
                <Text slot="description">{labelHint}</Text>

                {labelHintTooltipProps && (
                  <InputLabelTopTooltip
                    {...labelHintTooltipProps}
                    otherIconButtonProps={{
                      variant: "default20",
                      typographyVariant:
                        inputLabelTop.labelHint.typography.variant,
                    }}
                  />
                )}
              </Typography>
            )}
          </div>
        )}

        {children}
      </S.Container>
    );
  }
);

InputLabelTopV2.displayName = "InputLabelTopV2";

export default InputLabelTopV2;
